/**
 * Constrói as linhas de texto para cópia do lead.
 * Usa campos padrão (com rótulo da config ou padrão) e Campos extras configurados pela campanha.
 */

import {
  DEFAULT_FIELD_LABELS,
  LEAD_QUALITY_FIELD_OPTIONS,
  LEAD_VISIBLE_FIELD_OPTIONS,
} from "../constants/lead-visible-fields";
import {
  CampaignVisibleFieldsConfig,
  LeadContact,
} from "../types/lead-contacts.types";

export interface CopyLine {
  fieldId: string;
  line: string;
}

function formatDate(dateString: string | undefined): string {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date
      .toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      .replace(",", " às");
  } catch {
    return "";
  }
}

function getLabel(
  config: CampaignVisibleFieldsConfig | null | undefined,
  fieldId: string,
): string {
  const custom = config?.field_labels?.[fieldId];
  if (custom != null && String(custom).trim() !== "")
    return String(custom).trim();
  return (DEFAULT_FIELD_LABELS as Record<string, string>)[fieldId] ?? fieldId;
}

export function buildCopyLines(
  contact: LeadContact,
  extras: Record<string, string>,
  config: CampaignVisibleFieldsConfig | null | undefined,
): CopyLine[] {
  const visibleStandard =
    config?.visible_fields ?? LEAD_VISIBLE_FIELD_OPTIONS.map((f) => f.id);
  /** Mapa id (padrão) ou key (extra) -> CopyLine, para ordenação por field_order. */
  const byId = new Map<string, CopyLine>();
  /** Ordem padrão quando field_order não está definido. */
  const defaultOrder: CopyLine[] = [];

  const add = (fieldId: string, line: string) => {
    if (!visibleStandard.includes(fieldId)) return;
    const copyLine: CopyLine = { fieldId, line };
    byId.set(fieldId, copyLine);
    defaultOrder.push(copyLine);
  };

  if (contact.fixed) {
    add("fixed", "LEAD ALTERADO\n");
  }
  add(
    "campaign_name",
    `*${getLabel(config, "campaign_name")}:* ${contact.campaign_name}`,
  );
  add(
    "store",
    `*${getLabel(config, "store")}:* ${contact.store || "NÃO INFORMADO"}`,
  );

  const statusValue = contact.is_quality_lead
    ? (contact.store_status ?? contact.status)
    : contact.status;
  add("status", `*${getLabel(config, "status")}:* ${statusValue}`);
  add(
    "client_name",
    `*${getLabel(config, "client_name")}:* ${contact.valid_name || contact.name}`,
  );
  add("lead_id", `*${getLabel(config, "lead_id")}:* #${contact.lead_id}`);
  add(
    "telephone",
    `*${getLabel(config, "telephone")}:* ${contact.valid_telephone || contact.telephone}`,
  );

  if (contact.attendant_name) {
    add(
      "attendant_name",
      `*${getLabel(config, "attendant_name")}:* ${contact.attendant_name}`,
    );
  }
  if (contact.coordinator_name) {
    add(
      "coordinator_name",
      `*${getLabel(config, "coordinator_name")}:* ${contact.coordinator_name}`,
    );
  }

  add(
    "vehicle",
    `*${getLabel(config, "vehicle")}:* ${contact.vehicle || "SEM MODELO ESPECÍFICO"}`,
  );
  add(
    "vehicle_exchange",
    `*${getLabel(config, "vehicle_exchange")}:* ${contact.vehicle_exchange || "NÃO"}`,
  );
  add(
    "vehicle_financing",
    `*${getLabel(config, "vehicle_financing")}:* ${contact.vehicle_financing || "NÃO"}`,
  );
  add(
    "vehicle_estimated_purchase",
    `*${getLabel(config, "vehicle_estimated_purchase")}:* ${contact.vehicle_estimated_purchase || "NÃO INFORMADO"}`,
  );

  if (contact.scheduled_date) {
    add(
      "scheduled_date",
      `*${getLabel(config, "scheduled_date")}:* ${formatDate(contact.scheduled_date)}`,
    );
  }
  if (contact.call_back_date) {
    add(
      "call_back_date",
      `*${getLabel(config, "call_back_date")}:* ${formatDate(contact.call_back_date)}`,
    );
  }
  if (contact.note) {
    add("note", `*${getLabel(config, "note")}:* ${contact.note}`);
  }

  const extraFieldsList = config?.extra_fields ?? [];
  for (const { key, label } of extraFieldsList) {
    const value = extras[key];
    if (value) {
      const copyLine: CopyLine = {
        fieldId: `extra:${key}`,
        line: `*${label || key}:* ${value}`,
      };
      byId.set(key, copyLine);
      defaultOrder.push(copyLine);
    }
  }

  // Campos exclusivos de leads_quality: sempre incluir no preview/cópia quando is_quality_lead (rótulos da config ou padrão)
  if (contact.is_quality_lead) {
    const qualityValue = (key: string): string => {
      const v = (contact as unknown as Record<string, unknown>)[key];
      if (v == null || v === "") return "";
      if (key === "new_call_back_date" || key === "new_scheduled_date")
        return formatDate(String(v));
      return String(v);
    };
    const qualityConfigMap = new Map<string, string>();
    (config?.quality_fields ?? []).forEach(({ key, label }) => {
      qualityConfigMap.set(key, label || key);
    });
    for (const opt of LEAD_QUALITY_FIELD_OPTIONS as readonly {
      id: string;
      label: string;
    }[]) {
      const value = qualityValue(opt.id);
      if (value) {
        const label =
          qualityConfigMap.get(opt.id)?.trim() || opt.label || opt.id;
        const copyLine: CopyLine = {
          fieldId: `quality:${opt.id}`,
          line: `*${label}:* ${value}`,
        };
        byId.set(opt.id, copyLine);
        defaultOrder.push(copyLine);
      }
    }
  }

  const order = config?.field_order;
  if (order?.length) {
    return order
      .map((id) => byId.get(id))
      .filter((line): line is CopyLine => line != null);
  }
  return defaultOrder;
}

/** Filtra linhas pelos campos permitidos. Se visibleFields for null/undefined/vazio, retorna todas. */
export function filterCopyLines(
  lines: CopyLine[],
  visibleFields: string[] | null | undefined,
): CopyLine[] {
  if (visibleFields == null || visibleFields.length === 0) return lines;
  const set = new Set(visibleFields);
  return lines.filter(
    (l) => set.has(l.fieldId) || l.fieldId.startsWith("extra:"),
  );
}

export function copyLinesToText(lines: CopyLine[]): string {
  return lines.map((l) => l.line).join("\n");
}
